import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import styled from "@emotion/styled";
import { default as Button } from "../../../../../components/common/button/Button";
import { setColor } from "../../../../../assets/styles/styles";
import Modal from "../../../components/Modal";
import JsonViewerModal from "./JsonViewerModal";
import { createVanityLinkDataWithFiles } from "../../../../../api/VanityLinkController";
import UploadFile from "../../../../../components/common/fileUploader/FileUploaderInput";
import ToggleSwitch from "../../../../../components/common/switchToggle/ToggleSwitch";
import LoadingIcon from "../../../../../components/common/loading/LoadingIcon";

const AddVanityLinkData = ({
    roomId,
    roomSettingsId,
    setStepNext,
    vanityId,
    setSuccessMessageBanner,
    setBannerType,
    clientName,
}) => {
    const token = useSelector((state) => state?.user?.token);
    const [selectedBGImg, setSelectedBGImg] = useState(null);
    const [selectedMiscImg, setSelectedMiscImg] = useState(null);
    const [selectedClientImg, setSelectedClientImg] = useState(null);
    const [selectedClientLogoImg, setSelectedClientLogoImg] = useState(null);
    const [isJsonEditorOpen, setIsJsonEditorOpen] = useState(false);
    const [jsonEditorValue, setJsonEditorValue] = useState("");
    const [freeFormJson, setFreeFormJson] = useState("");
    const [ssoMetadataJson, setSsoMetadataJson] = useState("");
    const [isJsonEditorOpenSSO, setIsJsonEditorOpenSSO] = useState(false);
    const [emailLoginEnabled, setEmailLoginEnabled] = useState(true);
    const [fbLoginEnabled, setFbLoginEnabled] = useState(false);
    const [googleLoginEnabled, setGoogleLoginEnabled] = useState(false);
    const [twiiterLoginEnabled, setTwiiterLoginEnabled] = useState(false);
    const [spotifyLoginEnabled, setSpotifyLoginEnabled] = useState(false);
    const [otherLoginEnabled, setOtherLoginEnabled] = useState(false);
    const [sso, setSso] = useState(false);
    const [broadcast, setBroadcast] = useState(false);
    const [ambientTrackId, setAmbientTrackId] = useState("");
    const [guestOnlyMode, setGuestOnlyMode] = useState(false);
    const [marketingOptIn, setMarketingOptIn] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleJsonSave = async (value, field) => {
        if (field === "freeFormJson") setFreeFormJson(JSON.stringify(value));
        if (field === "ssoMetadataJson") setSsoMetadataJson(JSON.stringify(value));
    };

    const CreateVanityLinkDAtaAPI = async () => {
        setIsLoading(true);
        const data = {
            vanityLinkId: vanityId,
            clientName,
            freeformJson: freeFormJson || {},
            ssoMetadataJson: ssoMetadataJson || {},
            emailLogin: emailLoginEnabled,
            fbLogin: fbLoginEnabled,
            googleLogin: googleLoginEnabled,
            twitterLogin: twiiterLoginEnabled,
            spotifyLogin: spotifyLoginEnabled,
            otherLogin: otherLoginEnabled,
            sso,
            broadcast,
            guestOnlyMode,
            marketingOptIn,
        };
        const formData = new FormData();
        formData.append("vanityLinkData", JSON.stringify(data));

        if (selectedBGImg) {
            formData.append("bgImage", selectedBGImg);
        }

        if (selectedMiscImg) {
            formData.append("miscImage", selectedMiscImg);
        }

        if (selectedClientImg) {
            formData.append("otherLoginImg", selectedClientImg);
        }

        if (selectedClientLogoImg) {
            formData.append("logo", selectedClientLogoImg);
        }
        const response = await createVanityLinkDataWithFiles(formData, token);

        if (response?.status === 201) {
            setBannerType("SUCCESS");
            setSuccessMessageBanner(`Vanity Link Data Created Successfully.`);
            setStepNext("allStepsCompleted");
        } else if (response?.status === 400) {
            setBannerType("ERROR");
            if (response?.response?.data?.message) {
                setSuccessMessageBanner(`${response?.response?.data?.message}. Vanity Link Data already exists.`);
            } else {
                setSuccessMessageBanner(`Error Creating Vanity Link Data. Please try again.`);
            }
        } else {
            setBannerType("ERROR");
            setSuccessMessageBanner(`Error Creating Vanity Link Data. Please try again`);
        }
        setIsLoading(false);
    };

    return (
        <>
            <Table>
                <TableRow>
                    <TableColumn>
                        <Label>Room ID</Label>
                        <InputBox type="text" id="roomId" disabled value={roomId} />
                    </TableColumn>
                    <TableColumn>
                        <Label>Room Settings ID</Label>
                        <InputBox type="text" id="roomIdSettings" disabled value={roomSettingsId} />
                    </TableColumn>
                </TableRow>
                <TableRow>
                    <TableColumn>
                        <Label>Vanity Link ID</Label>
                        <InputBox type="text" id="vanityId" disabled value={vanityId} />
                    </TableColumn>
                </TableRow>
                <TableRow>
                    <TableColumn>
                        <Label>Client Name*</Label>
                        <InputBox
                            type="text"
                            id="clientName"
                            disabled
                            placeholder="Enter Client Name"
                            value={clientName}
                        />
                    </TableColumn>
                </TableRow>

                <TableRow>
                    <TableColumn>
                        <Label>FreeForm Json</Label>
                        <InputBox
                            type="text"
                            id="freeFormJson"
                            placeholder="Enter FreeForm Json"
                            value={freeFormJson}
                            onClick={() => setIsJsonEditorOpen(true)}
                        />
                    </TableColumn>
                </TableRow>

                <UploadFile
                    label="Vanity Background Image"
                    accept={["image/png", "image/jpeg", "image/jpg"]}
                    selectedFile={selectedBGImg}
                    setSelectedFile={setSelectedBGImg}
                    source="withoutCallingFileUploadAPI"
                />

                <UploadFile
                    label="Miscellaneous Image"
                    accept={["image/png", "image/jpeg", "image/jpg"]}
                    selectedFile={selectedMiscImg}
                    setSelectedFile={setSelectedMiscImg}
                    source="withoutCallingFileUploadAPI"
                />
                <UploadFile
                    label="Client Login Image"
                    accept={["image/png", "image/jpeg", "image/jpg"]}
                    selectedFile={selectedClientImg}
                    setSelectedFile={setSelectedClientImg}
                    source="withoutCallingFileUploadAPI"
                />
                <UploadFile
                    label="Client Logo Image"
                    accept={["image/png", "image/jpeg", "image/jpg"]}
                    selectedFile={selectedClientLogoImg}
                    setSelectedFile={setSelectedClientLogoImg}
                    source="withoutCallingFileUploadAPI"
                />

                <TableRow>
                    <TableColumn>
                        <Label>SSO Metadata Json</Label>
                        <InputBox
                            type="text"
                            id="ssoMetadataJson"
                            placeholder="Enter SSO Metadata Json"
                            value={ssoMetadataJson}
                            onClick={() => setIsJsonEditorOpenSSO(true)}
                        />
                    </TableColumn>

                    {/* <TableColumn>
                        <Label> Ambient Track ID </Label>
                        <InputBox
                            type="text"
                            id="ambientTrackId"
                            placeholder="Enter Ambient Track ID"
                            value={ambientTrackId}
                            onChange={(e) => setAmbientTrackId(e.target.value)}
                        />
                    </TableColumn> */}
                </TableRow>
                <TableRow>
                    <TableColumn>
                        <Label>
                            Email Login <br />
                            <span style={{ color: setColor.spanTextColor, fontSize: "12px", fontWeight: "400" }}>
                                {" "}
                                (Keep on unless alt client login selected)
                            </span>{" "}
                        </Label>
                        <ToggleSwitch value={emailLoginEnabled} onChange={(e) => setEmailLoginEnabled(e)} />
                    </TableColumn>
                    <TableColumn>
                        <Label>Facebook Login</Label>
                        <ToggleSwitch value={fbLoginEnabled} onChange={(e) => setFbLoginEnabled(e)} />
                    </TableColumn>
                    <TableColumn>
                        <Label>Google Login</Label>
                        <ToggleSwitch value={googleLoginEnabled} onChange={(e) => setGoogleLoginEnabled(e)} />
                    </TableColumn>
                    <TableColumn>
                        <Label>Twitter Login</Label>
                        <ToggleSwitch value={twiiterLoginEnabled} onChange={(e) => setTwiiterLoginEnabled(e)} />
                    </TableColumn>
                </TableRow>

                <TableRow>
                    <TableColumn>
                        <Label> Spotify Login </Label>
                        <ToggleSwitch value={spotifyLoginEnabled} onChange={(e) => setSpotifyLoginEnabled(e)} />
                    </TableColumn>
                    <TableColumn>
                        <Label> Other/Client Login </Label>
                        <ToggleSwitch value={otherLoginEnabled} onChange={(e) => setOtherLoginEnabled(e)} />
                    </TableColumn>
                    <TableColumn>
                        <Label>Broadcast</Label>
                        <ToggleSwitch value={broadcast} onChange={(e) => setBroadcast(e)} />
                    </TableColumn>
                    <TableColumn>
                        <Label>SSO</Label>
                        <ToggleSwitch value={sso} onChange={(e) => setSso(e)} />
                    </TableColumn>
                </TableRow>

                <TableRow>
                    <TableColumn>
                        <Label> Guest Only Mode </Label>
                        <ToggleSwitch value={guestOnlyMode} onChange={(e) => setGuestOnlyMode(e)} />
                    </TableColumn>

                    <TableColumn>
                        <Label> Marketing-Opt-In </Label>
                        <ToggleSwitch value={marketingOptIn} onChange={(e) => setMarketingOptIn(e)} />
                    </TableColumn>
                    <TableColumn>{""} </TableColumn>
                    <TableColumn>{""} </TableColumn>
                </TableRow>

                {isLoading && (
                    <TableRow>
                        <LoadingIcon />
                    </TableRow>
                )}
                <TableRow>
                    <Button
                        variant={"PinkButton"}
                        buttonWidth={"100%"}
                        disabled={!clientName || isLoading}
                        label={"Add Vanity Link Data → Complete Setup"}
                        onClick={CreateVanityLinkDAtaAPI}
                    />
                </TableRow>
            </Table>
            {isJsonEditorOpen && (
                <Modal
                    isOpen={isJsonEditorOpen}
                    setIsOpen={setIsJsonEditorOpen}
                    children={
                        <JsonViewerModal
                            jsonEditorValue={jsonEditorValue}
                            handleJsonSave={handleJsonSave}
                            fieldName={"freeFormJson"}
                            label={"FreeForm Json For Vanity Link Data"}
                            closeModal={() => setIsJsonEditorOpen(false)}
                        />
                    }
                />
            )}

            {isJsonEditorOpenSSO && (
                <Modal
                    isOpen={isJsonEditorOpenSSO}
                    setIsOpen={setIsJsonEditorOpenSSO}
                    children={
                        <JsonViewerModal
                            jsonEditorValue={jsonEditorValue}
                            handleJsonSave={handleJsonSave}
                            fieldName={"ssoMetadataJson"}
                            label={"SSO Metadata Json For Vanity Link Data"}
                            closeModal={() => setIsJsonEditorOpenSSO(false)}
                        />
                    }
                />
            )}
        </>
    );
};

export default AddVanityLinkData;

const Table = styled.div`
    display: flex;
    flex-direction: column;
    padding: 1rem;
    border-radius: 5px;
`;

const TableRow = styled.div`
    display: flex;
    flex-direction: row;
    margin-bottom: 10px;
`;

const TableColumn = styled.div`
    flex: 1;
    padding: 5px;
`;

const Label = styled.div`
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 10px;
`;

const InputBox = styled.input`
    padding: 10px;
    border: ${(props) => (props.errorMessage ? `1px solid ${setColor.errorColor}` : `1px solid ${setColor.grey1}`)};
    border-radius: 5px;
    color: ${setColor.inputText};
    margin: 0rem 0rem 0rem 0rem;
    width: ${(props) => (props.width ? props.width : "100%")};
    background-color: transparent;
    margin-bottom: ${(props) => (props.errorMessage ? "0.5rem" : "0rem")};
    &:disabled {
        background-color: ${setColor.grey1};
    }
`;
